import { useEffect } from 'react';

interface PreloadResource {
  href: string;
  as: 'font' | 'image' | 'style' | 'script';
  type?: string;
  crossorigin?: 'anonymous' | 'use-credentials';
}

/**
 * 预加载关键资源Hook
 * 优化Core Web Vitals指标
 */
export const usePreloadResources = (resources: PreloadResource[]) => {
  useEffect(() => {
    const links: HTMLLinkElement[] = [];
    
    resources.forEach((resource) => {
      // 检查是否已经存在相同的预加载链接
      const existingLink = document.querySelector(
        `link[rel="preload"][href="${resource.href}"]`
      );
      
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        
        if (resource.type) {
          link.type = resource.type;
        }
        
        if (resource.crossorigin) {
          link.crossOrigin = resource.crossorigin;
        }
        
        document.head.appendChild(link);
        links.push(link);
      }
    });

    // 清理函数，移除添加的预加载链接
    return () => {
      links.forEach((link) => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    };
  }, [resources]);
};

/**
 * DNS预取Hook
 * 预解析外部域名，减少DNS查找时间
 */
export const useDNSPrefetch = (domains: string[]) => {
  useEffect(() => {
    const links: HTMLLinkElement[] = [];
    
    domains.forEach((domain) => {
      const existingLink = document.querySelector(
        `link[rel="dns-prefetch"][href="${domain}"]`
      );
      
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'dns-prefetch';
        link.href = domain;
        
        document.head.appendChild(link);
        links.push(link);
      }
    });

    return () => {
      links.forEach((link) => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    };
  }, [domains]);
};

/**
 * 预连接Hook
 * 建立早期连接以减少关键资源的加载时间
 */
export const usePreconnect = (origins: string[]) => {
  useEffect(() => {
    const links: HTMLLinkElement[] = [];
    
    origins.forEach((origin) => {
      const existingLink = document.querySelector(
        `link[rel="preconnect"][href="${origin}"]`
      );
      
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        
        document.head.appendChild(link);
        links.push(link);
      }
    });

    return () => {
      links.forEach((link) => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    };
  }, [origins]);
};